import React, { type FC, useCallback } from 'react';
import { Formik, Form, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type UiHStackProps,
  UiStack,
  UiButton,
  UiText,
  UiLink,
  uiStyles,
  UiHStack, UiIconCaretRight,
} from '@/lib/ui';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type Registration, type RegistrationUpdateRequest, updateRegistration, registrationQueryKey } from '@/api/registration';
import CheckboxField from '@/base/Form/CheckboxField';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import PolicyCheckBox from '@/registration/component/Register/PolicyCheckBox';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

export interface InviteeFormProps extends UiHStackProps {
}

export interface FormData {
  isPrivacyPolicyAgreed: boolean
  isTermsAndConditionsAgreed: boolean
  // strict value
  privacyPolicyUrl?: string
  termsAndConditionsUrl?: string
}

const initFormData: FormData = {
  isPrivacyPolicyAgreed: false,
  isTermsAndConditionsAgreed: false
};

const formSchema = Yup.object().shape({
  privacyPolicyUrl: Yup.string().nullable(),
  termsAndConditionsUrl: Yup.string().nullable(),
  isPrivacyPolicyAgreed: Yup.boolean().when('privacyPolicyUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with the privacy policy before you continue'); }
  }),
  isTermsAndConditionsAgreed: Yup.boolean().when('termsAndConditionsUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with terms and condition before you continue'); }
  }),
});

const InviteeForm: FC<InviteeFormProps> = () => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest } = useTenantApi();
  const eventSettingQuery = useEventSettingsQuery(eventId);
  const { userSession } = useUserSession();
  const registrationQuery = useRegistrationQuery(eventId);
  const { toNextStepWithSteps } = useRegisterSteps();

  const { mutateAsync: updateRegistrationMutate, isLoading: isUpdateRegistrationLoading, error } = useMutation<ApiResponseSingle<Registration>, Error, RegistrationUpdateRequest>({
    mutationFn: async (data: RegistrationUpdateRequest) => {
      return await updateRegistration(createTenantApiRequest)(data);
    },
    onSuccess: (data: ApiResponseSingle<Registration>) => {
      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors.join(', '));
      }
      void queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
    },
    onError: (registrationError: any) => {
      throw registrationError;
    }
  });

  const onSubmit = useCallback(
    async (
      values: FormData,
      { setSubmitting }: FormikHelpers<FormData>
    ) => {
      setSubmitting(true);
      if (!registrationQuery.data) {
        throw new Error('Registration not found');
      }
      const data = await updateRegistrationMutate({
        registration: {
          id: registrationQuery.data.id,
          termsAndConditionsAccepted: values.isTermsAndConditionsAgreed,
          privacyPolicyAccepted: values.isPrivacyPolicyAgreed,
        }
      });
      setSubmitting(false);
      toNextStepWithSteps(data.item.registrationSteps);
    }, [registrationQuery.data, toNextStepWithSteps, updateRegistrationMutate]);

  return (
    <QueryContainer query={eventSettingQuery}>
      {(eventSetting) => {
        return (
          <Formik<FormData>
            initialValues={{
              ...initFormData,
              privacyPolicyUrl: eventSetting.privacy?.privacyPolicyUrl,
              termsAndConditionsUrl: eventSetting.privacy?.termsAndConditionsUrl,
              isPrivacyPolicyAgreed: registrationQuery?.data?.privacyPolicyAccepted ?? false,
              isTermsAndConditionsAgreed: registrationQuery?.data?.termsAndConditionsAccepted ?? false
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={formSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            <Form style={{ display: 'flex', flexDirection: 'column' }}>
              <BaseTransitionSlideLeft>
                <BaseFormFieldGroup>
                  {error && (
                    <UiStack spacing={4} flexGrow={1} py={4}>
                      <BaseMessageBarError>
                        {error.message ?? 'Failed to save the host.'}
                      </BaseMessageBarError>
                    </UiStack>
                  )}
                  <UiStack spacing={4} pb={4}>
                    {!!eventSetting?.privacy.privacyPolicyUrl && (
                      <PolicyCheckBox checkboxName='isPrivacyPolicyAgreed' policyText={'Privacy Policy'} policyUrl={eventSetting.privacy.privacyPolicyUrl} />
                    )}
                    {!!eventSetting?.privacy.termsAndConditionsUrl && (
                      <PolicyCheckBox checkboxName='isTermsAndConditionsAgreed' policyText={'Terms and Conditions'} policyUrl={eventSetting.privacy.termsAndConditionsUrl} />
                    )}
                  </UiStack>
                </BaseFormFieldGroup>
              </BaseTransitionSlideLeft>
              <UiStack height={96} />
              <LayoutFooter>
                {/*<UiHStack justifyContent={'flex-end'} flexGrow={1}>*/}
                {/*  <UiButton*/}
                {/*    px={8}*/}
                {/*    size={'lg'}*/}
                {/*    shadow={'base'}*/}
                {/*    colorScheme={'primary'}*/}
                {/*    type={'submit'}*/}
                {/*    isLoading={isUpdateRegistrationLoading}*/}
                {/*  >*/}
                {/*    Next*/}
                {/*  </UiButton>*/}
                {/*</UiHStack>*/}
                <UiStack width={24}/>
                <BrandFooter/>
                <BaseThemeButton
                  theme={'primary'}
                  type={'submit'}
                  isLoading={isUpdateRegistrationLoading}
                  rightIcon={(
                    <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                  )}
                >
                  Next
                </BaseThemeButton>
              </LayoutFooter>
            </Form>
          </Formik>
        );
      }}
    </QueryContainer>
  );
};

export default InviteeForm;
